import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'My Story',
  'Harm Reduction Win',
  'Lessons Learned',
  'Supporting a Friend',
  'Recovery Journey',
  'SA Nightlife Experience',
  'University Life'
];

const RealVoices = ({ userId }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: '',
    author: 'Anonymous'
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/real-voices`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPost.category) {
      toast.error('Please select a category');
      return;
    }

    try {
      await axios.post(`${API}/real-voices`, newPost);
      toast.success('Your story has been shared!');
      setShowForm(false);
      setNewPost({ title: '', content: '', category: '', author: 'Anonymous' });
      fetchPosts();
    } catch (error) {
      toast.error('Failed to share story');
    }
  };

  const handleUpvote = async (postId) => {
    try {
      await axios.post(`${API}/real-voices/${postId}/upvote`);
      fetchPosts();
    } catch (error) {
      console.error('Error upvoting:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white p-6 pb-24">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/')}
            className="bg-black text-[#FFD800] font-bold rounded-full px-6 py-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>

          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#FFD800] text-black font-bold rounded-full px-6 py-3 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="share-story-button"
            >
              <Send className="w-5 h-5 mr-2" />
              Share Your Voice
            </Button>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-5xl font-black text-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Real Voices
          </h1>
          <p className="text-black/70 text-lg" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Share experiences, learn from others, build community. All posts are moderated for safety and respect.
          </p>
        </motion.div>

        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#FFD800]/20 border-4 border-black rounded-3xl p-6 mb-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
          >
            <h2 className="text-3xl font-black text-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Share Your Story
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="share-story-form">
              <div>
                <Label className="text-black font-bold mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>Category</Label>
                <Select value={newPost.category} onValueChange={(value) => setNewPost({ ...newPost, category: value })}>
                  <SelectTrigger className="border-2 border-black rounded-xl bg-white" data-testid="category-select">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-2 border-black rounded-xl">
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat} className="rounded-lg">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-black font-bold mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>Title</Label>
                <Input
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  required
                  placeholder="Give your story a title"
                  className="border-2 border-black rounded-xl"
                  data-testid="story-title-input"
                />
              </div>

              <div>
                <Label className="text-black font-bold mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>Your Story</Label>
                <Textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  required
                  placeholder="Share your experience, lessons learned, or support for others..."
                  rows={6}
                  className="border-2 border-black rounded-xl resize-none"
                  data-testid="story-content-input"
                />
              </div>

              <div>
                <Label className="text-black font-bold mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>Name (Optional)</Label>
                <Input
                  value={newPost.author}
                  onChange={(e) => setNewPost({ ...newPost, author: e.target.value })}
                  placeholder="Anonymous"
                  className="border-2 border-black rounded-xl"
                  data-testid="story-author-input"
                />
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-white text-black border-2 border-black font-bold rounded-xl py-3 hover:bg-gray-100"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-black text-[#FFD800] font-bold rounded-xl py-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]"
                  style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                  data-testid="submit-story-button"
                >
                  Share Story
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        <div className="space-y-6">
          {posts.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white border-4 border-black rounded-2xl p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all"
              data-testid={`post-${post.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <span className="inline-block bg-[#FFD800] border-2 border-black px-3 py-1 rounded-full text-sm font-bold text-black mb-2">
                    {post.category}
                  </span>
                  <h3 className="text-2xl font-bold text-black" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {post.title}
                  </h3>
                  <p className="text-black/50 text-sm mt-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    By {post.author} • {new Date(post.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <p className="text-black leading-relaxed mb-4" style={{ fontFamily: 'Figtree, sans-serif' }}>
                {post.content}
              </p>

              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleUpvote(post.id)}
                  className="flex items-center gap-2 text-black hover:text-[#FF3B30] transition-colors"
                  data-testid={`upvote-${post.id}`}
                >
                  <Heart className="w-5 h-5" />
                  <span className="font-bold">{post.upvotes}</span>
                </button>
                <div className="flex items-center gap-2 text-black/50">
                  <MessageCircle className="w-5 h-5" />
                  <span className="font-bold">{post.comments_count}</span>
                </div>
              </div>
            </motion.div>
          ))}

          {posts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-black/50 text-lg mb-4" style={{ fontFamily: 'Figtree, sans-serif' }}>
                No stories yet. Be the first to share!
              </p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-[#FFD800] text-black font-bold rounded-full px-8 py-3 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              >
                Share Your Voice
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RealVoices;